import React, { useState } from "react";
import { X } from "lucide-react";
import letter from "../assets/letter.png";
import { isMobile } from "react-device-detect";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    inquiryType: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const WEB_APP_URL =
    "https://script.google.com/macros/s/AKfycbzGPSV23k71NtKdULS2QnfWKLy8z7xXs9jn9YKOSgvWvVSi_IY3sG0baEP5tXT8h9S4Jw/exec";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(WEB_APP_URL, {
        method: "POST",
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          inquiryType: formData.inquiryType,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.status === "success") {
        setSubmitStatus("success");
        setFormData({
          fullName: "",
          email: "",
          inquiryType: "",
          message: "",
        });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <section className="py-8 md:py-16 bg-white" id="contact">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
          {/* Image - Always first on mobile, second on desktop */}
          <div className={`${isMobile ? "mb-6" : ""} md:order-2 flex ${isMobile ? "justify-start pl-4" : "justify-center"}`}>
            <img
              src={letter}
              alt="Contact Illustration"
              className="w-auto h-auto max-w-full"
              style={{
                maxWidth: isMobile ? "120px" : "300px", // Reduced size on mobile to match other images
                height: "auto",
                objectFit: "contain"
              }}
            />
          </div>

          {/* Form - Always second on mobile, first on desktop */}
          <div className="md:order-1">
            <h2
              className="text-[#0E0444] text-xl md:text-2xl font-semibold"
              style={{
                fontFamily: "Inter",
                letterSpacing: "-0.025em",
                lineHeight: "1.4",
              }}
            >
              Have questions, feedback, or partnership ideas?
            </h2>
            <p
              className="mb-6 text-[#0E0444] text-xl md:text-2xl font-semibold"
              style={{
                fontFamily: "Inter",
                letterSpacing: "-0.025em",
                lineHeight: "1.4",
              }}
            >
              Get in touch with us—we're here to help!
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div>
                <label
                  htmlFor="fullName"
                  className="block mb-2 text-sm font-semibold text-[#4C4D5E]"
                  style={{
                    fontFamily: "Inter",
                  }}
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-md border border-gray-300 bg-gray-50"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block mb-2 text-sm font-semibold text-[#4C4D5E]"
                  style={{
                    fontFamily: "Inter",
                  }}
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-md border border-gray-300 bg-gray-50"
                  required
                />
              </div>

              {/* Inquiry Type */}
              <div>
                <label
                  htmlFor="inquiryType"
                  className="block mb-2 text-sm font-semibold text-[#4C4D5E]"
                  style={{
                    fontFamily: "Inter",
                  }}
                >
                  Inquiry Type
                </label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-md border border-gray-300 bg-gray-50 appearance-none"
                  required
                >
                  <option value="">Select</option>
                  <option value="general">General Inquiry</option>
                  <option value="partnership">Partnership</option>
                  <option value="support">Support</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block mb-2 text-sm font-semibold text-[#4C4D5E]"
                  style={{
                    fontFamily: "Inter",
                  }}
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Enter your message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full p-3 rounded-md border border-gray-300 bg-gray-50"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-[#0C60F2] text-white py-2.5 rounded-md font-semibold text-base"
                style={{
                  fontFamily: "Inter",
                  letterSpacing: "-0.01em",
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Get in Touch"}
              </button>

              {/* Status Messages */}
              {submitStatus === "success" && (
                <div className="text-green-600 text-center p-3 bg-green-50 rounded-md">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}
              {submitStatus === "error" && (
                <div className="text-red-600 text-center p-3 bg-red-50 rounded-md">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;